import React, { useEffect, useRef, useState } from "react";
import Axios from "axios";
import cookies from "js-cookie";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import Config from "../../Config";
import {
  forceRender,
  logout,
  setAllClear,
  setCurrentLocale,
  set_profile,
} from "../../../store/Actions/authActions";
import { WoshModal } from "../Feedback";
import "../Feedback/feedback.css";
import "./profile.css";
import { SUPPORT_URL } from "../../../utils/support";

/**
 * Profil (design « Profil.dc.html »).
 *
 * Mêmes appels que l'ancienne page (Profile/index.jsx) :
 *  - GET  /customer/profiles         lecture ;
 *  - PUT  auth                       prénom, nom, téléphone, entreprise, TVA ;
 *  - PUT  auth { locale }            langue, puis rechargement ;
 *  - PUT  auth { current_password… } mot de passe (Modals/EditProfile/ChangePass) ;
 *  - DELETE auth                     suppression (Profile/DeleteAccount) ;
 *  - logout()                        déconnexion (barre de navigation).
 *
 * Les champs modifiés font apparaître une barre « Enregistrer / Annuler » ;
 * la langue s'applique tout de suite, comme avant.
 */

export const FIELDS = ["first_name", "last_name", "phone_number", "company_name", "vat"];

export const LANGUAGES = [
  { cookie: "fn", api: "fr", label: "Français" },
  { cookie: "du", api: "nl", label: "Nederlands" },
  { cookie: "en", api: "en", label: "English" },
];

export const pickFields = (profile) =>
  FIELDS.reduce((values, key) => ({ ...values, [key]: profile?.[key] || "" }), {});

export const isDirty = (values, saved) =>
  FIELDS.some((key) => (values[key] || "") !== (saved[key] || ""));

export const initials = (first, last) =>
  `${(first || "").trim().charAt(0)}${(last || "").trim().charAt(0)}`.toUpperCase();

/** devise_token_auth : `errors.full_messages`, `errors` ou `message`. */
export const apiError = (data) => {
  const errors = data?.errors;

  if (Array.isArray(errors?.full_messages)) {
    return errors.full_messages.join(" ");
  }

  if (Array.isArray(errors)) {
    return errors.join(" ");
  }

  if (typeof data?.message === "string") {
    return data.message;
  }

  const user = data?.message?.user;

  return Array.isArray(user) ? user.join(" ") : null;
};

/* Pictos Ionicons du design (icons.js), en ligne. */
const PATHS = {
  lock: (
    <path d="M368 192h-16v-80a96 96 0 10-192 0v80h-16a64.07 64.07 0 00-64 64v176a64.07 64.07 0 0064 64h224a64.07 64.07 0 0064-64V256a64.07 64.07 0 00-64-64zm-48 0H192v-80a64 64 0 11128 0z" />
  ),
  shield: (
    <>
      <path className="wosh-prof__stroke" d="M336 176L225.2 304 176 255.8" />
      <path
        className="wosh-prof__stroke"
        d="M463.1 112.37C373.68 96.33 336.71 84.45 256 48c-80.71 36.45-117.68 48.33-207.1 64.37C32.7 369.13 240.58 457.79 256 464c15.42-6.21 223.3-94.87 207.1-351.63z"
      />
    </>
  ),
  chevron: (
    <path className="wosh-prof__stroke wosh-prof__stroke--bold" d="M184 112l144 144-144 144" />
  ),
  arrow: (
    <path
      className="wosh-prof__stroke wosh-prof__stroke--bold"
      d="M268 112l144 144-144 144M392 256H100"
    />
  ),
  check: (
    <path className="wosh-prof__stroke wosh-prof__stroke--check" d="M416 128L192 384l-96-96" />
  ),
};

const Icon = ({ name }) => (
  <svg viewBox="0 0 512 512" aria-hidden="true" fill="currentColor">
    {PATHS[name]}
  </svg>
);

const ProfilePage = ({ dispatch, authUser, render, history }) => {
  const { t } = useTranslation();

  const headers = {
    uid: authUser?.user_uid,
    client: authUser?.user_client,
    "access-token": authUser?.user_access_token,
  };

  const [profile, setProfile] = useState(null);
  const [saved, setSaved] = useState(pickFields(null));
  const [values, setValues] = useState(pickFields(null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(false);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordBusy, setPasswordBusy] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const timers = useRef([]);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  /* Profil illisible : on n'affiche pas un formulaire vide qu'on pourrait
     enregistrer par-dessus les vraies données. */
  const [loadFailed, setLoadFailed] = useState(false);

  const loadProfile = () => {
    setLoadFailed(false);
    Axios({ url: `${Config.apiURL}/customer/profiles`, method: "GET", headers })
      .then((response) => {
        const data = response?.data?.profile;

        setProfile(data);
        setSaved(pickFields(data));
        setValues(pickFields(data));
      })
      .catch(() => setLoadFailed(true));
  };

  useEffect(() => {
    loadProfile();

    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = isDirty(values, saved);
  const lang = cookies.get("i18next") || "en";
  const userId = profile?.wosh_user_id;

  const field = (key) => ({
    value: values[key],
    onChange: (event) => {
      setValues({ ...values, [key]: event.target.value });
      setSaveError(null);
      setNotice(null);
    },
  });

  const flash = (text) => {
    setNotice(text);
    later(() => setNotice(null), 2200);
  };

  /* ── Actions ─────────────────────────────────────────────── */

  const save = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      await Axios({
        url: `${Config.authURL}/auth`,
        method: "PUT",
        headers,
        data: FIELDS.reduce((data, key) => ({ ...data, [key]: values[key] }), {}),
      });
    } catch (err) {
      setSaving(false);
      setSaveError(apiError(err?.response?.data) || t("Your changes could not be saved."));
      return;
    }

    setSaving(false);
    setSaved(values);
    setProfile({ ...profile, ...values });
    flash(t("Profile updated"));
    /* Même rafraîchissement que l'ancienne page : nom dans la barre de navigation. */
    dispatch(forceRender(render !== 0 ? render + 1 : 1));
    dispatch(set_profile());
  };

  const reset = () => {
    setValues(saved);
    setSaveError(null);
  };

  const changeLanguage = async (language) => {
    if (language.cookie === lang) {
      return;
    }

    cookies.set("i18next", language.cookie);
    dispatch(setCurrentLocale({ ...authUser, user_locale: language.cookie }));

    try {
      await Axios({
        url: `${Config.authURL}/auth`,
        method: "PUT",
        headers,
        data: { locale: language.api },
      });
    } catch (err) {
      /* La langue de l'app change quand même ; comme avant. */
    }

    window.location.reload();
  };

  const copyId = () => {
    if (!userId) {
      return;
    }

    navigator.clipboard?.writeText(String(userId));
    setCopied(true);
    later(() => setCopied(false), 1500);
  };

  const passwordReady =
    passwords.current && passwords.next && passwords.next === passwords.confirm;

  const closePassword = () => {
    setPasswordOpen(false);
    setPasswords({ current: "", next: "", confirm: "" });
    setPasswordError(null);
  };

  const changePassword = async () => {
    if (!passwordReady || passwordBusy) {
      return;
    }

    setPasswordBusy(true);
    setPasswordError(null);

    try {
      await Axios({
        url: `${Config.authURL}/auth`,
        method: "PUT",
        headers,
        data: {
          current_password: passwords.current,
          password: passwords.next,
          password_confirmation: passwords.confirm,
        },
      });
    } catch (err) {
      setPasswordBusy(false);
      setPasswordError(
        apiError(err?.response?.data) || t("Your password could not be changed.")
      );
      return;
    }

    setPasswordBusy(false);
    closePassword();
    flash(t("Password changed"));
  };

  const deleteAccount = async () => {
    setDeleteError(null);

    try {
      const response = await Axios({
        url: `${Config.authURL}/auth`,
        method: "DELETE",
        headers,
      });

      if (response?.data?.message && !response?.data?.success) {
        setDeleteError(response.data.message);
        return;
      }
    } catch (err) {
      setDeleteError(
        apiError(err?.response?.data) || t("Your account could not be deleted.")
      );
      return;
    }

    /* Même sortie que Profile/DeleteAccount. */
    window.localStorage.clear();
    dispatch(setAllClear());
    history.push("/sign-in");
    window.location.reload();
  };

  const barText = saveError
    ? saveError
    : dirty
    ? t("Unsaved changes")
    : notice;

  /* ── Rendu ───────────────────────────────────────────────── */

  const input = (key, extra = {}) => (
    <input className="wosh-prof__input" id={`wosh-prof-${key}`} {...field(key)} {...extra} />
  );

  const head = (
    <div className="wosh-prof__head">
      <div className="wosh-prof__eyebrow">{t("My account")}</div>
      <h1 className="wosh-prof__title">{t("Profile")}</h1>
    </div>
  );

  if (loadFailed) {
    return (
      <div className="wosh-prof">
        <main className="wosh-prof__main">
          {head}
          <div className="wosh-prof__load-error" role="alert">
            <p>{t("We couldn't load your profile. Try again in a moment.")}</p>
            <button type="button" className="wosh-prof__bar-btn" onClick={loadProfile}>
              {t("Try again")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="wosh-prof">
      <main className="wosh-prof__main">
        {head}

        <section className="wosh-prof__hero">
          <div className="wosh-prof__avatar" aria-hidden="true">
            {initials(saved.first_name, saved.last_name)}
          </div>
          <div className="wosh-prof__who">
            <div className="wosh-prof__name">
              {`${saved.first_name} ${saved.last_name}`.trim()}
            </div>
            <div className="wosh-prof__mail">{profile?.email}</div>
          </div>
          <button
            type="button"
            className="wosh-prof__id"
            onClick={copyId}
            title={t("Copy")}
            aria-label={`${t("Customer ID")} ${userId || ""}. ${t("Copy")}`}
          >
            <span className="wosh-prof__id-label">
              {copied ? t("Copied") : t("CUSTOMER ID")}
            </span>
            <span className="wosh-prof__id-value">{userId || "—"}</span>
          </button>
        </section>

        <section className="wosh-prof__card">
          <h2 className="wosh-prof__card-title">{t("Personal information")}</h2>
          <div className="wosh-prof__grid">
            <label className="wosh-prof__field" htmlFor="wosh-prof-first_name">
              <span className="wosh-prof__label">{t("First Name")}</span>
              {input("first_name", { autoComplete: "given-name" })}
            </label>
            <label className="wosh-prof__field" htmlFor="wosh-prof-last_name">
              <span className="wosh-prof__label">{t("Last Name")}</span>
              {input("last_name", { autoComplete: "family-name" })}
            </label>
            <div className="wosh-prof__field">
              <span className="wosh-prof__label">{t("E-mail")}</span>
              <div className="wosh-prof__locked">
                <span>{profile?.email}</span>
                <Icon name="lock" />
              </div>
              <span className="wosh-prof__help">
                {t("To change your e-mail, contact")}{" "}
                <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                  {t("support")}
                </a>
                .
              </span>
            </div>
            <label className="wosh-prof__field" htmlFor="wosh-prof-phone_number">
              <span className="wosh-prof__label">{t("Phone")}</span>
              {input("phone_number", { type: "tel", autoComplete: "tel" })}
              <span className="wosh-prof__help">
                {t("The driver calls you on this number.")}
              </span>
            </label>
          </div>
        </section>

        <section className="wosh-prof__card">
          <h2 className="wosh-prof__card-title">{t("Billing information")}</h2>
          <div className="wosh-prof__grid">
            <label className="wosh-prof__field" htmlFor="wosh-prof-company_name">
              <span className="wosh-prof__label">{t("Business name")}</span>
              {input("company_name", { placeholder: "WOSH SRL", autoComplete: "organization" })}
            </label>
            <label className="wosh-prof__field" htmlFor="wosh-prof-vat">
              <span className="wosh-prof__label">{t("VAT number")}</span>
              {input("vat", { placeholder: "BE0123456789" })}
            </label>
          </div>
        </section>

        <section className="wosh-prof__card wosh-prof__card--row">
          <div className="wosh-prof__lang-text">
            <span className="wosh-prof__card-title">{t("Language")}</span>
            <span className="wosh-prof__sub">{t("App, e-mails and text messages.")}</span>
          </div>
          <div className="wosh-prof__segmented" role="radiogroup" aria-label={t("Language")}>
            {LANGUAGES.map((language) => (
              <button
                type="button"
                role="radio"
                aria-checked={language.cookie === lang}
                key={language.cookie}
                className={`wosh-prof__seg${
                  language.cookie === lang ? " wosh-prof__seg--on" : ""
                }`}
                onClick={() => changeLanguage(language)}
              >
                {language.label}
              </button>
            ))}
          </div>
        </section>

        <section className="wosh-prof__list">
          <button
            type="button"
            className="wosh-prof__row"
            onClick={() => setPasswordOpen(true)}
          >
            <Icon name="shield" />
            <span className="wosh-prof__row-label">{t("Change password")}</span>
            <span className="wosh-prof__row-end">
              <Icon name="chevron" />
            </span>
          </button>
          <div className="wosh-prof__divider" />
          <button
            type="button"
            className="wosh-prof__row"
            onClick={() => dispatch(logout(history))}
          >
            <Icon name="arrow" />
            <span className="wosh-prof__row-label">{t("Sign out")}</span>
          </button>
        </section>

        <button
          type="button"
          className="wosh-prof__delete"
          onClick={() => setDeleteOpen(true)}
        >
          {t("Delete my account")}
        </button>

        <a
          className="wosh-prof__legal"
          href="https://www.wosh.be/legal/terms-conditions"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("Terms & Conditions")}
        </a>
      </main>

      {(dirty || notice || saveError) && (
        <div
          className={`wosh-prof__bar${saveError ? " wosh-prof__bar--error" : ""}`}
          role="status"
        >
          <span className="wosh-prof__bar-text">{barText}</span>
          <div className="wosh-prof__bar-actions">
            {dirty ? (
              <>
                <button
                  type="button"
                  className="wosh-prof__bar-btn wosh-prof__bar-btn--ghost"
                  disabled={saving}
                  onClick={reset}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="wosh-prof__bar-btn"
                  disabled={saving}
                  onClick={save}
                >
                  {saving ? t("processing") : t("Save changes")}
                </button>
              </>
            ) : (
              notice && (
                <span className="wosh-prof__saved">
                  <Icon name="check" />
                  {t("Saved")}
                </span>
              )
            )}
          </div>
        </div>
      )}

      <WoshModal
        open={passwordOpen}
        onClose={closePassword}
        closable
        closeLabel={t("Close")}
        icon={<Icon name="shield" />}
        title={t("Change password")}
        error={passwordError}
        cancelLabel={t("Cancel")}
        confirmLabel={passwordBusy ? t("processing") : t("Save changes")}
        confirmDisabled={!passwordReady || passwordBusy}
        onConfirm={changePassword}
      >
        <div className="wosh-prof__pw">
          {[
            ["current", t("Current password"), "current-password"],
            ["next", t("New password"), "new-password"],
            ["confirm", t("Confirm the new password"), "new-password"],
          ].map(([key, label, autoComplete]) => (
            <label className="wosh-prof__field" key={key}>
              <span className="wosh-prof__label">{label}</span>
              <input
                className="wosh-prof__input"
                type="password"
                autoComplete={autoComplete}
                value={passwords[key]}
                onChange={(event) => {
                  setPasswords({ ...passwords, [key]: event.target.value });
                  setPasswordError(null);
                }}
              />
            </label>
          ))}
          {passwords.confirm && passwords.next !== passwords.confirm && (
            <span className="wosh-prof__mismatch">{t("Passwords do not match.")}</span>
          )}
        </div>
      </WoshModal>

      <WoshModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteError(null);
        }}
        closable
        closeLabel={t("Close")}
        title={t("Delete your account?")}
        warning={t("Your account will be deactivated and you will be signed out.")}
        error={deleteError}
        cancelLabel={t("No, keep it")}
        confirmLabel={t("Yes, delete")}
        onConfirm={deleteAccount}
      />
    </div>
  );
};

export { ProfilePage };

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
  render: state?.auth?.render,
});

export default connect(mapStateToProps)(ProfilePage);
